import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {WidgetGenericEvent} from 'features/core/shared/widget/event/widget-generic-event';
import {
    MvsWidgetFormGenericEventConstants
} from 'features/core/shared/widget/component/runtime/implementations/mvs-widget-form/mvs-widget-form-generic-event.constants';
import {MvsFormGroup} from 'features/core/shared/logic/form/mvs-form-group';
import {MvsFormDto} from 'features/core/shared/dto/form/dto/mvs-form.dto';
import {MvsFormFieldDto} from 'features/core/shared/dto/form/dto/mvs-form-field.dto';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {MvsObjectService} from 'features/core/shared/service/object/mvs-object.service';
import {MvsMessageService} from 'features/core/shared/service/message/mvs-message.service';
import {
    ObjectChangeInformation,
    ObjectChangeInformationActionEnum
} from 'features/core/shared/logic/object-change-information';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {ObjectRequestList} from 'features/core/shared/dto/object/request/object-request-list';
import {CpProvisionContractAgentService} from '../../../../../service/api/cp-provision-contract-agent.service';
import {
    ProvisionAmountOverwriteRequestDto,
    ProvisionAmountOverwriteResultDto
} from '../../../../../model/dto/api/provision-amount-overwrite.dto';
import {ProvisionContractAgentDto} from '../../../../../model/dto/entity/provision-contract-agent.dto';
import {ProvisionTypeDto} from '../../../../../model/dto/entity/provision-type.dto';
import {CpFeatureRight} from '../../../../../service/domain/cp-feature-right';

/**
 * Action dialog "Betrag überschreiben" (04-frontend.md §7) —
 * `POST /mvsa/cp/provisionContractAgents/{id}/overwriteAmount`; um right `cp_agentChange`
 * (AX-08 adds no new right).
 *
 * ── WHAT IT WRITES
 *
 * `provisionAmountOverwrite` plus its mandatory reason on ONE Provisionssatz. The overwrite is
 * recorded with `overwriteSource = MANUAL`, and a MANUAL overwrite is never touched again by a
 * recalculation (E6/A6-11) — which is exactly why the dialog insists on a reason and shows the
 * calculated amount next to the new one: the calculated `provisionAmount` stays on the row for
 * explainability (BR-065), and from here on the two will diverge permanently.
 *
 * ── THE THRESHOLD WARNING (AX-09)
 *
 * `ProvisionType.provisionAmountOverwriteThreshold` is the point above which an overwrite is
 * considered remarkable; the run additionally records message M-039 for it. The dialog reads the
 * threshold of the Satz's own provision type and warns as soon as the entered NET DIFFERENCE
 * exceeds it — before the write, not after. It is a WARNING, not a lock: 04 §7 asks for a
 * "Warnhinweis", and the operator with the right is allowed to proceed. The server is the one that
 * decides, and a business refusal arrives as HTTP 200 with `success = false`.
 *
 * ── FORM MECHANISM (design gate finding B-9 — mandatory documentation)
 *
 * TRANSIENT FORM WIDGET built from an `MvsFormDto` constructed in the frontend, identical to the
 * sibling dialogs of this OBC (repo evidence `wf-process-start.component.ts`). Neither `newAmount`
 * nor `reason` is a writable attribute of any entity form — `reason` exists in no table at all —
 * and `provisionAmountOverwrite` may ONLY be set through this endpoint, because only it enforces
 * the reason and stamps the provenance. So `mvs-crud-object` / generic CRUD is not an option here.
 * This component contains NO hand-written form HTML.
 */
@Component({
    selector: 'cp-provision-agent-amount-overwrite-dialog',
    templateUrl: './cp-provision-agent-amount-overwrite-dialog.component.html',
    styleUrls: ['./cp-provision-agent-amount-overwrite-dialog.component.scss'],
    standalone: false
})
export class CpProvisionAgentAmountOverwriteDialogComponent implements OnInit, OnChanges, OnDestroy {

    busy: boolean;
    initialized: boolean;

    /** The Provisionssatz whose amount is overwritten (`cp.ProvisionContractAgent`). */
    @Input() provisionContractAgentId: number;

    /** Display text of the Satz (Vertrag · Ereignis · Provisionsart); purely informational. */
    @Input() provisionContractAgentLabel: string;

    /** um right `cp_agentChange` (U-08) — optimistic default, demoted on 403; see CpFeatureRight. */
    @Input() canAgentChange: boolean = true;

    /** Visibility of the dialog; intended as a two-way binding (`[(visible)]`). */
    @Input() visible: boolean = false;
    @Output() visibleChange = new EventEmitter<boolean>();

    /** Fires after a successfully written overwrite. */
    @Output() onAmountOverwritten = new EventEmitter<ProvisionAmountOverwriteResultDto>();

    /** Fires when the server refused the right with a 403 — the demotion signal. */
    @Output() onForbidden = new EventEmitter<void>();

    /** The transient capture widget (see class Javadoc). */
    formWidget: WidgetData;

    /** Last VALID form value; null as long as the form is incomplete/invalid. */
    formValue: any = null;

    /** The Satz as last read — source of the calculated amount and of the current overwrite. */
    provisionContractAgentDto: ProvisionContractAgentDto = null;

    /** Threshold of the Satz's provision type (AX-09); null = none configured. */
    overwriteThreshold: number = null;

    /** Backend error message when `success = false`, or on a technical error. */
    failureMessage: string = null;

    constructor(
        protected coreService: MvsCoreService,
        protected mvsObjectService: MvsObjectService,
        protected messageService: MvsMessageService,
        protected provisionContractAgentService: CpProvisionContractAgentService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.initialized = true;
        if (this.visible) {
            this.refreshComponent();
        }
    }

    /**
     * Rebuilt fresh on every open. A form value left over from the previously selected Satz would
     * be written onto a different row here — the dialog is opened repeatedly from a list.
     */
    refreshComponent(): void {

        this.formValue = null;
        this.failureMessage = null;
        this.formWidget = null;
        this.provisionContractAgentDto = null;
        this.overwriteThreshold = null;

        if (!this.provisionContractAgentId) {
            return;
        }

        this.busy = true;
        this.loadContractAgent();
    }

    /**
     * Read the Satz, then its provision type's threshold.
     *
     * Both are needed BEFORE the form is useful: the calculated amount is what the new amount is
     * compared against, and the threshold is what decides whether the comparison is remarkable.
     * A failure on either leaves the dialog usable — the form is built regardless, only the
     * preview then has less to say.
     */
    private loadContractAgent(): void {

        const filterCriteria = [
            FilterCriteria.create('id', FilterCriteria.cOperatorEqual, this.provisionContractAgentId)
        ];

        this.coreService.getCrudService('cp.ProvisionContractAgent')
            .list(ObjectRequestList.createBasic(false, filterCriteria, null))
            .subscribe({
                next: (dtoList) => {
                    const entries = (dtoList?.entries as ProvisionContractAgentDto[]) ?? [];
                    this.provisionContractAgentDto = entries.length ? entries[0] : null;
                    this.loadThreshold();
                },
                error: () => {
                    this.provisionContractAgentDto = null;
                    this.buildFormWidget();
                    this.busy = false;
                }
            });
    }

    /** Read `provisionAmountOverwriteThreshold` of the Satz's provision type (AX-09). */
    private loadThreshold(): void {

        const provisionTypeId = this.provisionContractAgentDto?.provisionTypeDtoId;

        if (!provisionTypeId) {
            this.buildFormWidget();
            this.busy = false;
            return;
        }

        const filterCriteria = [
            FilterCriteria.create('id', FilterCriteria.cOperatorEqual, provisionTypeId)
        ];

        this.coreService.getCrudService('cp.ProvisionType')
            .list(ObjectRequestList.createBasic(false, filterCriteria, null))
            .subscribe({
                next: (dtoList) => {
                    const entries = (dtoList?.entries as ProvisionTypeDto[]) ?? [];
                    this.overwriteThreshold = entries.length
                        ? (entries[0].provisionAmountOverwriteThreshold ?? null)
                        : null;
                    this.buildFormWidget();
                    this.busy = false;
                },
                error: () => {
                    this.overwriteThreshold = null;
                    this.buildFormWidget();
                    this.busy = false;
                }
            });
    }

    /**
     * Build the `MvsFormDto` and the transient form widget from it. The field ids match the
     * attributes of {@link ProvisionAmountOverwriteRequestDto} 1:1, so the submit needs no
     * renaming logic.
     */
    private buildFormWidget(): void {

        const form = new MvsFormDto();
        const formFields: MvsFormFieldDto[] = [];

        // New amount — MANDATORY. Deliberately NOT pre-filled: `contextValue` is the only seeding
        // mechanism an MvsFormDto field has, and it HIDES the field it seeds in create mode
        // (MvsFormControlService), which would take the very field away that this dialog exists to
        // capture. The amount in force is shown next to the field in the dialog header instead.
        const newAmountField = MvsFormFieldDto.createBasic('newAmount', 'amount', 'Neuer Betrag');
        newAmountField.uiMandatory = true;
        newAmountField.order = 10;
        newAmountField.uiDescription =
            'Ersetzt den gerechneten Betrag als Soll dieses Satzes; der gerechnete Wert bleibt erhalten.';
        formFields['newAmount'] = newAmountField;

        // Reason — MANDATORY (Pflichtgrund, AX-09).
        const reasonField = MvsFormFieldDto.createBasic('reason', 'textarea', 'Grund');
        reasonField.uiMandatory = true;
        reasonField.order = 20;
        reasonField.uiDescription = 'Pflichtangabe: warum wird der gerechnete Betrag überschrieben?';
        formFields['reason'] = reasonField;

        form.formFields = formFields;

        /**
         * AI: Transient form widget for capturing an amount overwrite on a Provisionssatz.
         *     Source: MvsFormDto built in the frontend (reason exists in no table) — the write
         *     goes to POST /cp/provisionContractAgents/{id}/overwriteAmount.
         *     Caseworkers use it to replace the calculated commission amount of one rate row.
         */
        this.formWidget = WidgetFactory.createWidget(
            'cp.agent.amountOverwrite.form',
            'Betrag überschreiben',
            'form',
            'transient',
            'transient',
            'cp.ProvisionContractAgent',
            WidgetDataParam.create('size', 'S'),
            WidgetDataParam.create('form', form)
        );
    }

    /** Receives the form values; only a fully VALID form unlocks the submit button. */
    handleGenericEvent(event: WidgetGenericEvent): void {

        if (event.eventName !== MvsWidgetFormGenericEventConstants.EVENT_FORM_VALUE_CHANGE) {
            return;
        }

        const formGroup: MvsFormGroup =
            event.object[MvsWidgetFormGenericEventConstants.EVENT_FORM_VALUE_CHANGE_ATTR_FORM_GROUP];

        this.formValue = formGroup.status === 'VALID' ? formGroup.value : null;
        this.failureMessage = null;

        // The preview follows the ENTERED value, valid form or not: the operator must see the diff
        // while typing the amount, not only after the reason has been written.
        const rawAmount = formGroup.value?.['newAmount'];
        this.enteredAmount = (rawAmount === null || rawAmount === undefined || rawAmount === '')
            ? null
            : Number(rawAmount);
    }

    /** The amount currently entered in the form; null while the field is empty. */
    enteredAmount: number = null;

    /**
     * The amount in force today: the existing overwrite if there is one, otherwise the calculated
     * amount. This is what a new overwrite actually replaces, so it is what the diff is measured
     * against.
     */
    get currentAmount(): number {
        const dto = this.provisionContractAgentDto;
        if (!dto) {
            return null;
        }
        return (dto.provisionAmountOverwrite !== null && dto.provisionAmountOverwrite !== undefined)
            ? dto.provisionAmountOverwrite
            : (dto.provisionAmount ?? null);
    }

    /** The calculated amount, kept for explainability (BR-065) even once an overwrite exists. */
    get calculatedAmount(): number {
        return this.provisionContractAgentDto?.provisionAmount ?? null;
    }

    /** Net difference the overwrite produces; null while nothing comparable is entered. */
    get netDifference(): number {
        if (this.enteredAmount === null || this.currentAmount === null) {
            return null;
        }
        return this.enteredAmount - this.currentAmount;
    }

    /** True once the entered deviation exceeds the configured threshold (AX-09). */
    get exceedsThreshold(): boolean {
        const difference = this.netDifference;
        return this.overwriteThreshold !== null
            && difference !== null
            && Math.abs(difference) > this.overwriteThreshold;
    }

    /** Submit is open only with the right, a valid form and no request in flight. */
    get submittable(): boolean {
        return this.canAgentChange && !!this.formValue && !this.busy && !!this.provisionContractAgentId;
    }

    /** Why the submit button is locked, in German — one reason per cause. */
    get submitTooltip(): string {

        if (!this.canAgentChange) {
            return CpFeatureRight.deniedMessage(CpFeatureRight.AGENT_CHANGE);
        }

        if (!this.formValue) {
            return 'Bitte einen neuen Betrag und einen Grund angeben.';
        }

        return 'Betrag überschreiben.';
    }

    /**
     * Writes the overwrite.
     *
     * A business failure comes back as HTTP 200 with `success = false` (bm convention) — so
     * `success` is checked on EVERY response and 2xx is never read as "applied".
     */
    handleOverwriteAmount(): void {

        if (!this.submittable) {
            return;
        }

        const request: ProvisionAmountOverwriteRequestDto = {
            newAmount: Number(this.formValue['newAmount']),
            reason: this.formValue['reason']
        };

        this.busy = true;
        this.failureMessage = null;

        this.provisionContractAgentService.overwriteAmount(this.provisionContractAgentId, request).subscribe({
            next: (result: ProvisionAmountOverwriteResultDto) => {
                this.busy = false;

                if (!result || result.success !== true) {
                    this.failureMessage = result?.failureMessage
                        ?? 'Der Betrag konnte nicht überschrieben werden.';
                    return;
                }

                this.messageService.showSuccessMessage(
                    'Betrag überschrieben',
                    'Der neue Betrag ist ab dem nächsten Berechnungslauf das Soll dieses Satzes.');

                this.informObjectChanged();
                this.onAmountOverwritten.emit(result);
                this.handleClose();
            },
            error: (error) => {
                this.busy = false;
                this.demoteOnForbidden(error);
                this.failureMessage = CpFeatureRight.isForbidden(error)
                    ? CpFeatureRight.deniedMessage(CpFeatureRight.AGENT_CHANGE)
                    : 'Der Betrag konnte nicht überschrieben werden. Bitte Eingaben prüfen.';
            }
        });
    }

    /**
     * ONLY a 403 demotes: a 500 or a dropped connection must leave the flag alone, otherwise a
     * transient backend fault would silently take the action away from a user who holds the right.
     */
    private demoteOnForbidden(error: any): void {
        if (CpFeatureRight.isForbidden(error)) {
            this.canAgentChange = false;
            this.onForbidden.emit();
        }
    }

    /** Tell the module that this Provisionssatz changed — the hosting list reloads on it. */
    private informObjectChanged(): void {
        this.mvsObjectService.objectChangeEvent.next(
            ObjectChangeInformation.create(
                'cp.ProvisionContractAgent',
                ObjectChangeInformationActionEnum.updated,
                null,
                null));
    }

    handleShow(): void {
        this.refreshComponent();
    }

    handleClose(): void {
        this.visible = false;
        this.visibleChange.emit(false);
    }

    handleVisibleChange(value: boolean): void {
        this.visible = value;
        this.visibleChange.emit(value);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['visible'] && this.visible) {
            this.refreshComponent();
        }
        if (changes['provisionContractAgentId'] && this.visible) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
    }

}
